import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { appendCashReconLedgerCoaQueryParams, unwrapGlTransactionPage } from './cashReconLedgerScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateFormFieldsSx,
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateToolbarButtonSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const Output = () => {
  const { canExport } = useFunctionPermissions(FC.CASH_RECONCILIATION);
  const navigate = useNavigate();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedProperty, setSelectedProperty] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dataType, setDataType] = useState('mapped'); // 'mapped' or 'unmapped'
  const [properties, setProperties] = useState([]);
  const [glCodes, setGlCodes] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [glCodesLoading, setGlCodesLoading] = useState(false);
  
  // Data states
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  // Fetch properties list
  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      const apiUrl = `${baseUrl}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;
      
      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data && Array.isArray(data)) {
          const processedData = data.map((item, index) => ({
            id: item.propertyId || index + 1,
            propertyId: item.propertyId || '',
            propertyName: item.propertyName || '',
            propertyCode: item.propertyCode || '',
            propertyGroupId: item.propertyGroupId || '',
            companyId: item.companyId || '',
            addressLine1: item.addressLine1 || '',
            city: item.city || '',
            state: item.state || '',
            country: item.country || '',
            postalCode: item.postalCode || '',
            baseCurrency: item.baseCurrency || 'USD',
            defaultBankAccountId: item.defaultBankAccountId || null,
            cashReconEnabled: item.cashReconEnabled !== false,
            status: item.status || 'ACTIVE',
            deleted: item.deleted || false,
            createdAt: item.createdAt || '',
            updatedAt: item.updatedAt || '',
          }));
          setProperties(processedData);
        }
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Fetch GL Codes list
  const fetchGlCodes = async () => {
    try {
      setGlCodesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        return;
      }

      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      let apiUrl = `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}`;
      
      // Add property_id if a property is selected
      if (selectedProperty) {
        const selectedPropertyData = properties.find(p => p.propertyName === selectedProperty);
        if (selectedPropertyData) {
          apiUrl += `&property_id=${selectedPropertyData.propertyId}`;
        }
      }

      apiUrl = await appendCashReconLedgerCoaQueryParams(apiUrl, { baseUrl, tenantId, token });

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data && Array.isArray(data)) {
          setGlCodes(data);
        }
      }
    } catch (err) {
      console.error('Error fetching GL codes:', err);
      showError('Failed to fetch GL codes');
    } finally {
      setGlCodesLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchGlCodes();
  }, []);

  // Refetch GL codes when property changes
  useEffect(() => {
    if (properties.length > 0) {
      fetchGlCodes();
    }
  }, [selectedProperty, properties]);

  // Format date to YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get current year start date (January 1st of current year)
  const getYearStartDate = () => {
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);
    return formatDateLocal(yearStart);
  };

  // Get current quarter start date
  const getQuarterStartDate = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
    const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);
    return formatDateLocal(quarterStart);
  };

  // Get today's date
  const getTodayDate = () => {
    return formatDateLocal(new Date());
  };

  // Handle YTD button click
  const handleYTD = () => {
    setStartDate(getYearStartDate());
    setEndDate(getTodayDate());
  };

  // Handle QTD button click
  const handleQTD = () => {
    setStartDate(getQuarterStartDate());
    setEndDate(getTodayDate());
  };

  // Handle Load button click (stateOverride: optional 'mapped' | 'unmapped' when switching toggle)
  const handleLoad = async (page = 0, pageSize = 100, stateOverride = null) => {
    if (!selectedProperty || selectedProperty === '' || !startDate || !endDate) {
      showError('Please select Property, Start Date, and End Date');
      return;
    }


    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      const offset = page * pageSize;
      const limit = pageSize;

      // Find property ID from selected property name
      const selectedPropertyData = properties.find(p => p.propertyName === selectedProperty);
      const propertyId = selectedPropertyData ? selectedPropertyData.propertyId : '';

      // Build API URL based on data type (mapped or unmapped)
      const effectiveType = stateOverride !== null ? stateOverride : dataType;
      const state = effectiveType === 'mapped' ? 'mapped' : 'unmapped';
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      let apiUrl = await appendCashReconLedgerCoaQueryParams(
        `${baseUrl}conductor/cash-recon/gl-transactions/by-tenant-mapping-filter?tenant_id=${tenantId}&state=${state}&start_date=${startDate}&end_date=${endDate}&property_id=${propertyId}&limit=${limit}&offset=${offset}`
      , { baseUrl, tenantId, token });

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch data: ${resp.status} ${resp.statusText}`);
      }

      const pageResult = unwrapGlTransactionPage(await resp.json());
      
      if (pageResult) {
        // Process data for grid display
        const processedRows = pageResult.content.map((item, index) => {
          // Calculate amount: use debit if credit is 0, otherwise use credit
          const amount = (item.debitAmount && item.debitAmount !== 0 && (!item.creditAmount || item.creditAmount === 0))
            ? item.debitAmount
            : (item.creditAmount || 0);
          
          return {
            id: item.id || offset + index + 1,
            property: selectedProperty || '',
            description: item.description || '',
            date: item.transactionDate || '',
            amount: amount,
            glCode: item.glAccountName || '',
            originalData: item, // Keep original data for reference
          };
        });
        
        setRows(processedRows);
        setRowCount(pageResult.total);
        showSuccess(`Successfully loaded ${processedRows.length} of ${pageResult.total} ${effectiveType} record(s)`);
      } else {
        throw new Error('Invalid response format from API');
      }
      
    } catch (err) {
      setError(err.message);
      setRows([]);
      setRowCount(0);
      showError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle data type toggle change — auto-load when filters are already set
  const handleDataTypeChange = (event, newDataType) => {
    if (newDataType !== null) {
      setDataType(newDataType);
      setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
      setRows([]);
      setRowCount(0);
      // If filters are set, load the new type immediately (no Load click needed)
      if (selectedProperty && selectedProperty !== '' && startDate && endDate) {
        handleLoad(0, paginationModel.pageSize, newDataType);
      }
    }
  };

  // Handle Export to Excel
  const handleExportToExcel = async () => {
    if (!canExport) return;
    if (!selectedProperty || selectedProperty === '' || !startDate || !endDate) {
      showError('Please select Property, Start Date, and End Date before exporting');
      return;
    }


    if (rows.length === 0) {
      showError('No data to export. Please load data first');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }

      const selectedPropertyData = properties.find((p) => p.propertyName === selectedProperty);
      const propertyId = selectedPropertyData ? selectedPropertyData.propertyId : '';
      const state = dataType === 'mapped' ? 'mapped' : 'unmapped';

      // Use the same Conductor API as handleLoad so export matches the grid (mapped + unmapped).
      // Previously export called FastAPI mapped-bank-reference-data with tenant_integration +
      // property_name, which often returned no rows while the grid had data from this endpoint.
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';
      let allExportData = [];
      const chunkSize = 1000;
      let offset = 0;
      let total = Number.POSITIVE_INFINITY;
      let guard = 0;

      while (offset < total && guard < 10000) {
        guard += 1;
        const apiUrl = await appendCashReconLedgerCoaQueryParams(
          `${baseUrl}conductor/cash-recon/gl-transactions/by-tenant-mapping-filter?tenant_id=${tenantId}&state=${state}&start_date=${startDate}&end_date=${endDate}&property_id=${propertyId}&limit=${chunkSize}&offset=${offset}`
        , { baseUrl, tenantId, token });

        const resp = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!resp.ok) {
          let errorMessage = `Failed to fetch data for export: ${resp.status} ${resp.statusText}`;
          try {
            const errorData = await resp.json();
            if (errorData.message || errorData.detail) {
              errorMessage = errorData.message || errorData.detail || errorMessage;
            }
          } catch (e) {
            // keep default message
          }
          throw new Error(errorMessage);
        }

        const page = unwrapGlTransactionPage(await resp.json());

        if (!page) {
          throw new Error('Invalid response format from API');
        }

        const chunkData = page.content.map((item) => {
          const amount =
            item.debitAmount &&
            item.debitAmount !== 0 &&
            (!item.creditAmount || item.creditAmount === 0)
              ? item.debitAmount
              : item.creditAmount || 0;

          return {
            Property: selectedProperty || '',
            Description: item.description || '',
            Date: item.transactionDate || '',
            Amount: amount,
            'GL Code': item.glAccountName || '',
          };
        });

        allExportData = [...allExportData, ...chunkData];
        total = page.total;
        if (page.content.length === 0) break;
        offset += page.content.length;
      }

      if (allExportData.length === 0) {
        showError('No data available to export');
        return;
      }

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(allExportData);

      // Set column widths
      const columnWidths = [
        { wch: 20 }, // Property
        { wch: 30 }, // Description
        { wch: 12 }, // Date
        { wch: 15 }, // Amount
        { wch: 15 }, // GL Code
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      const sheetName = `Cash_Recon_${dataType === 'mapped' ? 'Mapped' : 'Unmapped'}`;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.substring(0, 31));

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Cash_Reconciliation_${dataType === 'mapped' ? 'Mapped' : 'Unmapped'}_${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);

      showSuccess(`Successfully exported ${allExportData.length} record(s) to ${filename}`, 'Export Complete');
      
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      showError(err.message || 'Failed to export data to Excel');
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination change
  useEffect(() => {
    if (rows.length > 0 || paginationModel.page > 0) {
      handleLoad(paginationModel.page, paginationModel.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  // Grid columns
  const columns = [
    // {
    //   field: 'property',
    //   headerName: 'Property',
    //   flex: 1,
    //   minWidth: 150,
    // },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 80,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 80,
      type: 'number',
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'glCode',
      headerName: 'GL Code',
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        height: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={harmonatePlainIconButtonSx}
            aria-label="Back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={harmonatePageTitleSx}>
            Cash Reconciliation - Output
          </Typography>
        </Box>
        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleExportToExcel}
            disabled={loading || rows.length === 0 || !selectedProperty || !startDate || !endDate || !canExport}
            startIcon={<FileDownloadIcon />}
            sx={harmonateToolbarButtonSx}
          >
            Export to Excel
          </Button>
          {/* Data Type Toggle */}
          <ToggleButtonGroup
            value={dataType}
            exclusive
            onChange={handleDataTypeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 2,
                py: 0.5,
                borderColor: ht.line,
                color: ht.text,
                '&.Mui-selected': {
                  bgcolor: ht.green,
                  color: ht.white,
                  '&:hover': {
                    bgcolor: ht.green,
                  },
                },
                '&:hover': {
                  bgcolor: ht.secondaryTab,
                },
              },
            }}
          >
            <ToggleButton value="mapped">Mapped</ToggleButton>
            <ToggleButton value="unmapped">Unmapped</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ ...harmonateCardSx, ...harmonateFormFieldsSx, p: 2, mb: 2, flexShrink: 0 }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            disabled={propertiesLoading}
            options={properties}
            getOptionLabel={(option) => {
              const code = option.propertyCode || '';
              const name = option.propertyName || '';
              return code && name ? `${code} - ${name}` : name;
            }}
            value={properties.find(p => p.propertyName === selectedProperty) || null}
            onChange={(event, newValue) => {
              setSelectedProperty(newValue ? newValue.propertyName : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property / Hotel"
                size="small"
                placeholder="Select Property"
                sx={{ minWidth: 300 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {propertiesLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.propertyName === value?.propertyName}
            sx={{ fontSize: '14px' }}
          />


          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleYTD}
              sx={harmonateUploadOutlinedSx}
            >
              YTD
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleQTD}
              sx={harmonateUploadOutlinedSx}
            >
              QTD
            </Button>
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
              handleLoad(0, paginationModel.pageSize);
            }}
            disabled={loading || !selectedProperty || !startDate || !endDate}
            sx={harmonateProceedButtonSx}
          >
            {loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Load'}
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Display Area - grid always visible like Compare/Reclassification */}
      <Paper
        elevation={0}
        sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={40}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0 }}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  Select filters and click Load to view {dataType} data
                </Typography>
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Toast Notification */}
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default Output;

